// The transaction-interaction graph: a point-in-time, DOM-free model of how the
// currently-open transactions overlap through the memory they read and write.
//
// It is built from a single `DebugSnapshot` (the world at the selected trace row)
// plus the retained MVCC `Store`. Transactions and memory locations are both
// nodes; each read / write / waited-on address is an edge from a transaction to a
// location. On top of that bare bipartite graph we compute the three things the
// view exists to reveal:
//
//   * why a transaction will abort — a read whose address has been committed over
//     since the txn took its snapshot (`GraphEdge.stale` / `TxnNode.willAbort`);
//   * what a `retry`-parked transaction is waiting for — its read set, drawn as
//     `waiting` edges (`TxnNode.parked === "retry"`);
//   * which transaction can unpark it — a live write to an address some parked txn
//     waits on (`GraphEdge.wakes` / `AddrNode.wakes`).
//
// This module is pure so it can be unit-tested without React; `GraphView` renders
// whatever it returns.

import type { DebugSnapshot, DebugThread } from "../src/backend/interp/debug.js";
import type { Store } from "../src/backend/interp/store.js";
import type { TraceEvent } from "../src/backend/interp/trace.js";
import type { Address } from "../src/backend/interp/value.js";
import { describeKey, type ObjectTypes } from "./trace-layout.js";

/** A transaction node — one per live thread that is in, or parked on, a txn. */
export interface TxnNode {
  /** Stable node id, `txn:<id>`. */
  id: string;
  txnId: number;
  thread: number;
  /** The thread's entry function, for a human label (`T1 · transfer`). */
  entryFn: string;
  /** Whether the thread produced the selected event (the "▸" acting thread). */
  acting: boolean;
  /** The txn's phase when it isn't a plainly-running OCC txn:
   *   - "retry"     — parked on its read set, waiting for a committed write;
   *   - "lock"      — blocked waiting to acquire its 2PL footprint;
   *   - "lock-held" — holds its 2PL footprint (any time from acquire to release,
   *                   including while its begun body runs);
   *   - null        — a running OCC txn (begun; reads/writes as usual). */
  parked: "retry" | "lock" | "lock-held" | null;
  /** A running txn with at least one stale read: it would abort if it committed now.
   *  This is a *prediction* about a still-open txn; on the txn's own commit/abort
   *  event `outcome` is set instead and takes precedence (see `outcome`). */
  willAbort: boolean;
  /** The known result when the selected event is this txn's own commit/abort — it
   *  overrides the speculative `willAbort`, so a committing txn never shows as
   *  "will abort" just because the store already reflects its own write. null on
   *  every other event (the txn is still in flight). */
  outcome: "commit" | "abort" | null;
  /** The action this txn is taking *at the selected event* — "lock" on its own
   *  `lock-acquire` row, "unlock" on its own `lock-release` row, "retry" on its own
   *  `txn-retry` row, "orelse" on an `orelse-retry` (a branch blocked; the next one
   *  is tried). This is the action being performed this step, so it takes
   *  precedence over the resting state (e.g. "holding · lock"). null otherwise. */
  action: "lock" | "unlock" | "retry" | "orelse" | null;
  /** The attempt this txn replaces, on the row where it takes over: a txn that was
   *  abandoned by a `retry` park (now woken and re-running under this new id) or by
   *  an abort. The predecessor appears as a `ghost` node with a `retry` edge into
   *  this one, so the hand-off is visible. null otherwise. */
  retryOf: number | null;
  /** A node shown only to anchor a `retry` edge: the previous attempt, which is no
   *  longer an open transaction. Drawn faded — it is history, not live state. */
  ghost: boolean;
  readCount: number;
  writeCount: number;
}

/** A memory-location node — one per address any open txn touches. */
export interface AddrNode {
  /** Stable node id, `addr:<address>`. */
  id: string;
  addr: Address;
  /** Typed, human-readable form of the address, e.g. `/player/2`. */
  label: string;
  /** Distinct open txns reading this address (includes parked waiters). */
  readers: number;
  /** Distinct open txns writing this address. */
  writers: number;
  /** Written by one txn while read/written by another — the shape of a conflict. */
  contended: boolean;
  /** A parked txn is waiting on this address and a live txn is writing it: the
   *  write that will wake the waiter lands here. */
  wakes: boolean;
}

export type EdgeKind = "read" | "write" | "waiting" | "lock" | "retry";

/** A read / write / waited-on / held-lock relationship from a transaction to a
 *  location — or, for `retry`, the hand-off from an abandoned attempt to the
 *  transaction that replaces it (the only txn→txn edge). */
export interface GraphEdge {
  id: string;
  /** Source `TxnNode.id`. */
  from: string;
  /** Target `AddrNode.id` — or a `TxnNode.id` when `kind` is `retry`. */
  to: string;
  kind: EdgeKind;
  /** A read whose address was committed over since the txn's snapshot — the
   *  reason a running txn must abort. Set on `read` edges, and on the `write`
   *  edge of a read-modify-write whose read dependency has gone stale. */
  stale: boolean;
  /** A write to an address some parked txn is waiting on — it will unpark that
   *  waiter on commit. Only ever set on `write` edges. */
  wakes: boolean;
}

export interface TxnGraph {
  txns: TxnNode[];
  addrs: AddrNode[];
  edges: GraphEdge[];
}

/** One thread's participation in the graph, normalized across the running and the
 *  blocked cases: a running thread contributes its live txn's read/write sets (and,
 *  under 2PL, the footprint it still holds); an abandoned attempt its read set (see
 *  `participations`); a lock-blocked thread the footprint it is acquiring. */
interface Participation {
  thread: DebugThread;
  txnId: number;
  parked: "retry" | "lock" | "lock-held" | null;
  reads: Address[];
  writes: Address[];
  /** The 2PL footprint currently held. Addresses here that aren't read/written yet
   *  are drawn as held-lock edges — so a lock txn shows what it holds for its whole
   *  lifetime, including the begun body before it has touched a cell. */
  held: Address[];
  /** The snapshot version to validate reads against; null when unknown (parked). */
  readVersion: number | null;
  /** A finished attempt shown only for the hand-off (see `TxnNode.ghost`). Its
   *  edges are drawn, but it is excluded from a location's reader/writer tallies —
   *  it is history, and shouldn't make an address look live-contended. */
  ghost?: boolean;
  /** For a ghost: how it ended, which decides its label and outline. */
  ended?: "abort";
}

/** A thread's participations. Usually one, but on the row a woken attempt hands
 *  off it is two: the `retry`-abandoned attempt (still carrying the read set it
 *  parked on) followed by the fresh transaction replacing it. */
function participations(thread: DebugThread): Participation[] {
  const out: Participation[] = [];
  const pa = thread.prevAttempt;
  if (pa && pa.txn !== thread.txn?.id) {
    // While the thread has no live transaction this abandoned attempt *is* its
    // current state (a `retry` park, waiting on its reads). Once the replacement
    // has begun, it is history — a ghost, listed first so it lands directly above
    // the successor its hand-off arrow points to.
    const ghost = thread.txn != null;
    out.push(
      pa.ended === "retry"
        ? {
            thread,
            txnId: pa.txn,
            parked: "retry",
            reads: pa.reads,
            // Its tentative writes were rolled back, and it isn't attempting
            // anything while it waits — only what it waits *on* is meaningful.
            writes: [],
            held: [],
            readVersion: null,
            ghost,
          }
        : {
            thread,
            txnId: pa.txn,
            parked: null,
            reads: pa.reads,
            writes: pa.writes, // what it was attempting when it lost the race
            held: [],
            // Its snapshot version is kept so the read that went stale — the reason
            // it aborted — is still identifiable on the hand-off row.
            readVersion: pa.readVersion,
            ghost,
            ended: "abort" as const,
          }
    );
  }
  const live = participation(thread);
  if (live) out.push(live);
  return out;
}

function participation(thread: DebugThread): Participation | null {
  if (thread.txn) {
    // A begun txn. Under 2PL it also still holds its footprint (`holding`); an OCC
    // txn holds nothing. A held footprint marks it as a lock txn ("lock-held").
    const held = thread.holding?.addrs ?? [];
    return {
      thread,
      txnId: thread.txn.id,
      parked: held.length > 0 ? "lock-held" : null,
      reads: thread.txn.reads,
      writes: thread.txn.writes.map((w) => w.addr),
      held,
      readVersion: thread.txn.readVersion,
    };
  }
  const h = thread.holding;
  if (h) {
    // A 2PL txn that holds its footprint but hasn't begun (or has committed and is
    // about to release): its held footprint shows as held-lock edges. Checked
    // before `waiting` — holding is the further-along state.
    return { thread, txnId: h.txn, parked: "lock-held", reads: [], writes: [], held: h.addrs, readVersion: null };
  }
  const w = thread.waiting;
  if (w?.kind === "lock") {
    // A lock waiter is blocked acquiring its whole footprint; model those as the
    // addresses it is waiting on so the view shows what it is contending for.
    return { thread, txnId: w.txn, parked: "lock", reads: w.addrs, writes: [], held: [], readVersion: null };
  }
  return null;
}

/**
 * Build the transaction-interaction graph for the world captured in `snap`.
 * Returns empty node lists when no thread is in (or parked on) a transaction.
 *
 * `event` is the selected trace event. When it is a txn's own commit or abort,
 * that txn's node gets a definite `outcome` (and a committing txn's speculative
 * staleness is suppressed) — otherwise the snapshot at a commit event, which the
 * interpreter records *after* the txn's own write has advanced the store, would
 * make the txn's own committed write look like a conflicting newer version and
 * mislabel a successful commit as "will abort".
 */
export function buildTxnGraph(
  snap: DebugSnapshot,
  store: Store,
  types?: ObjectTypes,
  event?: TraceEvent | null
): TxnGraph {
  const parts = snap.threads.flatMap(participations);

  const committingTxn = event?.type === "txn-commit" ? event.txn : null;
  const abortingTxn = event?.type === "txn-abort" ? event.txn : null;
  const lockingTxn = event?.type === "lock-acquire" ? event.txn : null;
  const unlockingTxn = event?.type === "lock-release" ? event.txn : null;
  const retryingTxn = event?.type === "txn-retry" ? event.txn : null;
  const orElseTxn = event?.type === "orelse-retry" ? event.txn : null;
  // A re-run taking over from the attempt it replaces, on its `begin` row.
  const reborn =
    event?.type === "txn-begin" && event.prevTxn !== undefined
      ? { prev: event.prevTxn, next: event.txn }
      : null;

  // Addresses any parked (retry) txn is waiting to read — the set a live write
  // must overlap to unpark someone. Used to flag wake writes and wake locations.
  const waitedOn = new Set<Address>();
  for (const p of parts) {
    if (p.parked === "retry") for (const a of p.reads) waitedOn.add(a);
  }

  // Per-address tallies, accumulated as we walk each txn's footprint.
  const addrInfo = new Map<Address, { readers: Set<number>; writers: Set<number> }>();
  const bump = (addr: Address): { readers: Set<number>; writers: Set<number> } => {
    let info = addrInfo.get(addr);
    if (!info) {
      info = { readers: new Set(), writers: new Set() };
      addrInfo.set(addr, info);
    }
    return info;
  };

  const edges: GraphEdge[] = [];
  const txns: TxnNode[] = [];

  for (const p of parts) {
    const writeSet = new Set(p.writes);
    const readSet = new Set(p.reads);
    // This txn's known result at the selected event, if this event is its own
    // commit/abort. A committing txn validated successfully, so its reads are not
    // stale — the store already carries its own write, which we must not read as
    // a conflict.
    const outcome: "commit" | "abort" | null =
      p.txnId === committingTxn
        ? "commit"
        : p.txnId === abortingTxn || p.ended === "abort"
        ? "abort"
        : null;
    // The lock action taken this step (its own acquire/release row), which reads
    // as an action rather than the resting "holding" state.
    const action: "lock" | "unlock" | "retry" | "orelse" | null =
      p.txnId === lockingTxn
        ? "lock"
        : p.txnId === unlockingTxn
        ? "unlock"
        : p.txnId === retryingTxn
        ? "retry"
        : p.txnId === orElseTxn
        ? "orelse"
        : null;
    let willAbort = false;

    // One edge per (txn, address). A written address is drawn as a `write`; an
    // address only read as a `read` (or `waiting`, for a parked waiter). Reading
    // *and* writing the same cell collapses to the single, stronger write edge —
    // its mutation is what makes the location contended.
    const touched = new Set<Address>([...p.reads, ...p.writes]);
    for (const addr of touched) {
      const info = bump(addr);
      const written = writeSet.has(addr);
      // A ghost still gets its location node and edges, but doesn't count as a
      // live reader/writer — a finished attempt shouldn't make an address look
      // contended by something that is no longer running.
      if (!p.ghost) {
        if (written) info.writers.add(p.txnId);
        else info.readers.add(p.txnId);
      }

      // A touched address is a real read/write even for a lock txn; only the
      // *untouched* held footprint is drawn as `lock` (see the held loop below).
      let kind: EdgeKind;
      if (written) kind = "write";
      else if (p.parked === "retry" || p.parked === "lock") kind = "waiting"; // blocked
      else kind = "read";

      // A running txn's read is stale if the address it read has a committed
      // version newer than the txn's snapshot — someone changed data it depends
      // on, so validation at commit would fail. This is judged on read-set
      // membership, not the drawn edge kind: a read-modify-write (the common
      // abort case) collapses to a `write` edge but its read dependency can
      // still be stale, and that is exactly the abort we want to surface.
      // Point-in-time: bounded by what had committed as of this snapshot.
      const stale =
        outcome !== "commit" &&
        readSet.has(addr) &&
        p.readVersion !== null &&
        store.latestBeginAsOf(addr, snap.committedVersion) > p.readVersion;
      if (stale) willAbort = true;

      const wakes = written && waitedOn.has(addr);

      edges.push({
        id: `${p.txnId}->${addr}:${kind}`,
        from: `txn:${p.txnId}`,
        to: `addr:${addr}`,
        kind,
        stale,
        wakes,
      });
    }

    // Held 2PL footprint not yet read/written: a `lock` edge, so the txn shows
    // what it holds even before (and between) touching those cells. A held lock is
    // exclusive, so it isn't tallied as a reader/writer — but the node must exist.
    for (const addr of p.held) {
      if (touched.has(addr)) continue;
      bump(addr);
      edges.push({
        id: `${p.txnId}->${addr}:lock`,
        from: `txn:${p.txnId}`,
        to: `addr:${addr}`,
        kind: "lock",
        stale: false,
        wakes: false,
      });
    }

    txns.push({
      id: `txn:${p.txnId}`,
      txnId: p.txnId,
      thread: p.thread.id,
      entryFn: p.thread.entryFn,
      acting: p.thread.id === snap.actingThread,
      parked: p.parked,
      willAbort,
      outcome,
      action,
      retryOf: reborn && p.txnId === reborn.next ? reborn.prev : null,
      ghost: p.ghost ?? false,
      readCount: p.reads.length,
      writeCount: p.writes.length,
    });
  }

  // The hand-off, on the row a re-run takes over: the attempt it replaces is no
  // longer an open txn, so it is added as a ghost node with a `retry` edge into its
  // successor — making the "same work, new transaction" linkage visible.
  if (reborn) {
    const nextAt = txns.findIndex((t) => t.txnId === reborn.next);
    const prevAt = txns.findIndex((t) => t.txnId === reborn.prev);
    if (nextAt >= 0 && prevAt >= 0) {
      // The abandoned attempt is still in the snapshot (a `retry` park keeps its
      // read set), so it needs no synthesizing — just mark it as history and link
      // it, keeping the waiting edges that show what it was parked on.
      txns[prevAt]!.ghost = true;
      edges.push({
        id: `${reborn.prev}->${reborn.next}:retry`,
        from: `txn:${reborn.prev}`,
        to: `txn:${reborn.next}`,
        kind: "retry",
        stale: false,
        wakes: false,
      });
    } else if (nextAt >= 0) {
      const next = txns[nextAt]!;
      // Inserted directly above its successor so the edge is a short vertical hop.
      txns.splice(nextAt, 0, {
        id: `txn:${reborn.prev}`,
        txnId: reborn.prev,
        thread: next.thread, // a re-run continues on the same thread
        entryFn: next.entryFn,
        acting: false,
        parked: null,
        willAbort: false,
        outcome: null,
        action: null,
        retryOf: null,
        ghost: true,
        readCount: 0,
        writeCount: 0,
      });
      edges.push({
        id: `${reborn.prev}->${reborn.next}:retry`,
        from: `txn:${reborn.prev}`,
        to: `txn:${reborn.next}`,
        kind: "retry",
        stale: false,
        wakes: false,
      });
    }
  }

  const addrs: AddrNode[] = [...addrInfo.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([addr, info]) => {
      const readers = info.readers.size;
      const writers = info.writers.size;
      return {
        id: `addr:${addr}`,
        addr,
        label: describeKey(addr, types),
        readers,
        writers,
        // Contended: a writer plus at least one other distinct participant.
        contended: writers > 0 && readers + writers > 1,
        wakes: writers > 0 && waitedOn.has(addr),
      };
    });

  return { txns, addrs, edges };
}
